/** @jsxImportSource ../i18n */
import React, { Component, Suspense, lazy, memo, useEffect, useRef, useState } from 'react';
import { MascotAvatar, mascotById } from './mascotCatalog';
import { MOOD_LABELS, MOOD_MASCOT_STATE } from '../services/expertMoods';

const Mascotte = lazy(() => import('./Mascotte'));
const MascotteSpine = lazy(() => import('./MascotteSpine'));

const SPOTLIGHT_QUERY = '(min-width: 1024px) and (prefers-reduced-motion: no-preference)';

const MOOD_TEXT = {
  speaking: 'text-gold-300',
  thinking: 'text-fg-body',
  conflict: 'text-warn-300',
  aligned: 'text-gain-400',
  listening: 'text-fg-subtle',
};

// One seat around the table. Memoized on primitive props: text fragments
// arriving every 400 ms never re-render it, only a mood change does.
const Seat = memo(({ name, mascot, mood, reason }) => (
  <li className={`seat-${mood} flex w-32 flex-col items-center gap-1.5 text-center lg:w-24`} data-testid="seat" data-expert={name} data-mood={mood} title={reason || undefined}>
    <div className="relative">
      <span className="seat-halo pointer-events-none absolute -inset-1.5 rounded-3xl shadow-[0_0_24px_rgb(var(--c-gold-500)/0.35)] ring-2 ring-gold-400/80" aria-hidden="true" />
      <div className="seat-avatar"><MascotAvatar variant={mascot} name={name} size="seat" /></div>
      {mood === 'thinking' && (
        <span className="absolute -right-4 -top-3 flex gap-1 rounded-full border border-line-strong bg-surface-3 px-2 py-1.5" aria-hidden="true">
          <span className="seat-dot h-1.5 w-1.5 rounded-full bg-fg-body" /><span className="seat-dot h-1.5 w-1.5 rounded-full bg-fg-body" /><span className="seat-dot h-1.5 w-1.5 rounded-full bg-fg-body" />
        </span>
      )}
    </div>
    <span className="max-w-full truncate text-sm font-medium text-fg">{name}</span>
    <span className={`text-xs leading-tight ${MOOD_TEXT[mood]}`}>{MOOD_LABELS[mood]}</span>
  </li>
));

class SpotlightBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const useMedia = (query) => {
  const [matches, setMatches] = useState(() => Boolean(window.matchMedia?.(query)?.matches));
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
};

// A single animated mascot (one WebGL canvas) for the expert holding the
// floor, under the seats in the sticky side column: it stays in view while
// the feed scrolls and never covers a seat. Its height only follows the
// viewport, as the canvas only resizes with the window.
const Spotlight = memo(({ name, mascot, mood }) => {
  const state = MOOD_MASCOT_STATE[mood] || 'idle';
  return (
    <figure className="hidden lg:flex flex-col items-center w-full pointer-events-none" data-testid="spotlight" data-expert={name} data-state={state}>
      <div className="w-full max-w-[300px] h-[clamp(12rem,calc(100vh-35rem),23.75rem)]">
        <SpotlightBoundary key={mascot}>
          <Suspense fallback={null}>
            {mascot === 'gladys-manga'
              ? <MascotteSpine state={state} framing="bust" />
              : <Mascotte key={mascot} variant={mascot} state={state} />}
          </Suspense>
        </SpotlightBoundary>
      </div>
      <figcaption className="mt-2 text-sm text-fg-muted">{name} · {MOOD_LABELS[mood]}</figcaption>
    </figure>
  );
});

// The panel around the table during the live debate: every expert's seat
// with its deterministic mood, and the speaker's animated mascot.
const DebateTable = ({ members, moods }) => {
  const spotlightAllowed = useMedia(SPOTLIGHT_QUERY);
  // Keep the last speaker in the spotlight between two turns instead of
  // unmounting and reloading a mascot for every short silence.
  const speaker = members.find((m) => ['speaking', 'thinking'].includes(moods[m.name]?.mood));
  const lastSpeaker = useRef(null);
  if (speaker) lastSpeaker.current = speaker.name;
  const featured = members.find((m) => m.name === lastSpeaker.current);

  if (members.length === 0) return null;
  return (
    <section className="flex flex-col items-center gap-5 rounded-card border border-line bg-surface-1 p-6 shadow-card lg:px-4" aria-label="Table ronde des experts">
      <ul className="flex flex-wrap justify-center gap-6 lg:gap-3" data-testid="debate-table">
        {members.map((m) => (
          <Seat key={m.name} name={m.name} mascot={m.mascot} mood={moods[m.name]?.mood || 'listening'} reason={moods[m.name]?.reason || ''} />
        ))}
      </ul>
      {spotlightAllowed && featured && mascotById(featured.mascot) && (
        <Spotlight name={featured.name} mascot={featured.mascot} mood={moods[featured.name]?.mood || 'listening'} />
      )}
    </section>
  );
};

export default memo(DebateTable);
